import { useSize } from '@cibo/ui/src/hooks/useSize'
import { Box, Stack, Typography } from '@mui/material'
import * as d3 from 'd3'
import numeral from 'numeral'
import { filter, groupBy, map, mapObjIndexed, omit, pipe, pluck, prop, toPairs } from 'ramda'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import { InventoryCsvRow } from '../../types'

const PALETTE = ['#5fa7f1', '#Aece5f', '#bbd2f1']
const KG_PER_METRIC_TON = 907.18

type EmissionsByCategoryProps = {
  height: number
  csvData: Array<InventoryCsvRow>
  commodity: string
  year: number
}

type TotalEmissionRow = InventoryCsvRow & { kgCO2Equiv: number }

export const EmissionsByCategory = ({
  height,
  csvData,
  commodity,
  year,
}: EmissionsByCategoryProps) => {
  const { t } = useTranslation('impact/EmissionsByCategory')

  const graphId = useMemo(() => `graph-${uuid()}`, [])
  const sizeTarget = useRef<HTMLElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  const size = useSize(sizeTarget, 100)

  const { width } = size || { width: 0 }

  const colorScale = d3.scaleOrdinal(PALETTE)

  const categoryEmissions = pipe<
    InventoryCsvRow[],
    InventoryCsvRow[],
    TotalEmissionRow[],
    Record<string, TotalEmissionRow[]>,
    Record<string, TotalEmissionRow[]>,
    Record<string, number>,
    Array<[string, number]>,
    Array<{ name: string; value: number }>
  >(
    filter((d: InventoryCsvRow) => d.commodity === commodity && d.year === year),
    map(row => ({
      ...row,
      // @Todo: use acresSourcedFromSupplyShed instead of totalTillableManagedAcres
      kgCO2Equiv:
        row.totalTillableManagedAcres * row.yieldKgPerAcre * row.kgCO2EquivPerKgTotalYield,
    })),
    groupBy(prop('emissionsCategory')),
    omit(['Removals', 'none']),
    mapObjIndexed(rows => pluck('kgCO2Equiv')(rows).reduce((a, b) => a + b, 0)),
    toPairs,
    map(([name, value]) => ({ name, value }))
  )(csvData)

  const pie = d3
    .pie<{ name: string; value: number }>()
    .padAngle(0.025)
    .value((d: any) => d.value)
  const arc = d3
    .arc<d3.PieArcDatum<{ name: string; value: number }>>()
    .innerRadius(60)
    .outerRadius(74)

  const arcs = pie(categoryEmissions)

  const xScale = d3
    .scaleLinear()
    .domain([0, pluck('value', categoryEmissions).reduce((a, b) => a + b, 0)])
    .range([0, width - height])

  return (
    <Box p={2}>
      <Stack ref={sizeTarget} direction="row" alignItems="center">
        <Box>
          <svg ref={svgRef} id={graphId} width={height} height={height}>
            <g transform={`translate(${height / 2},${height / 2})`}>
              {arcs.map(a => (
                <path key={a.data.name} d={arc(a) || undefined} fill={colorScale(a.data.name)} />
              ))}
            </g>
          </svg>
        </Box>

        <Stack spacing={3} sx={{ flex: 1 }}>
          {categoryEmissions.map(({ name, value }) => (
            <Stack key={name} spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography>{t(name)}</Typography>
                <Typography>{numeral(value / KG_PER_METRIC_TON).format('0,0.00')}</Typography>
              </Stack>

              <div
                style={{
                  position: 'relative',
                  backgroundColor: '#99999933',
                  height: 4,
                  width: width - height,
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    backgroundColor: colorScale(name),
                    height: 4,
                    width: xScale(value),
                  }}
                />
              </div>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  )
}
